import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useServers } from '@/hooks/use-servers';
import { useProjects } from '@/hooks/use-projects';
import { useDeploymentOverview } from '@/hooks/use-deployments';
import { useVaultItems } from '@/hooks/use-vault';
import { frameCopy } from '@/components/layout/frame-copy';
import { spatialDockOverflow, spatialDockPrimary } from '@/lib/spatial-nav';
import { quoteNpcArg, spatialNpcCommand } from '@/lib/spatial-route';
import type { Server } from '@/api/types';

interface PaletteItem {
  id: string;
  group: string;
  label: string;
  href?: string;
  cmd?: string;
  hint?: string;
}

export function CommandPalette({ open, onClose }: { open: boolean; onClose: () => void }) {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const copy = frameCopy(i18n.language);
  const [query, setQuery] = useState('');
  const [index, setIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const servers = useServers({ page: 1, pageSize: 100, sortBy: 'name', sortDirection: 'asc' });
  const projects = useProjects({ page: 1, pageSize: 100 });
  const overview = useDeploymentOverview();
  const vault = useVaultItems({ page: 1, pageSize: 50 });

  const items = useMemo(() => {
    const list: PaletteItem[] = [];
    for (const item of spatialDockPrimary) {
      list.push({
        id: `nav-${item.to}`,
        group: 'page',
        label: t(item.labelKey),
        href: item.to,
        cmd: spatialNpcCommand(item.to),
      });
    }
    for (const section of spatialDockOverflow()) {
      for (const item of section.items) {
        list.push({
          id: `nav-${item.to}`,
          group: t(section.labelKey),
          label: t(item.labelKey),
          href: item.to,
          cmd: spatialNpcCommand(item.to),
        });
      }
    }
    for (const server of (servers.data?.data ?? []) as Server[]) {
      list.push({
        id: `srv-${server.id}`,
        group: 'host',
        label: server.name,
        hint: server.host,
        href: `/servers/${server.id}`,
        cmd: `npc server handoff brief ${quoteNpcArg(server.name)} --section agentSummary`,
      });
    }
    for (const project of projects.data?.data ?? []) {
      list.push({
        id: `prj-${project.id}`,
        group: 'project',
        label: project.name,
        href: `/projects/${project.id}`,
        cmd: `npc project handoff get ${quoteNpcArg(project.id)}`,
      });
    }
    for (const run of overview.data?.recentRuns ?? []) {
      list.push({
        id: `run-${run.id}`,
        group: 'run',
        label: `${run.projectName}${run.environmentName ? ` / ${run.environmentName}` : ''}`,
        hint: run.status,
        href: `/deployments/runs/${run.id}`,
        cmd: run.status === 'failed'
          ? `npc deploy retry ${quoteNpcArg(run.id)} --wait`
          : `npc deploy ${quoteNpcArg(run.projectId)} ${quoteNpcArg(run.environmentName || '<env>')} --ref ${quoteNpcArg(run.gitRef || 'main')} --wait`,
      });
    }
    for (const item of vault.data?.data ?? []) {
      list.push({
        id: `vault-${item.id}`,
        group: 'vault',
        label: item.name,
        hint: item.kind,
        href: '/secrets',
      });
    }
    const terms = query.trim().toLowerCase().replace(/^npc\s*/, '').split(/\s+/).filter(Boolean);
    return list
      .filter((item) => terms.every((term) => `${item.label} ${item.cmd ?? ''} ${item.hint ?? ''} ${item.group}`.toLowerCase().includes(term)))
      .slice(0, 40);
  }, [overview.data?.recentRuns, projects.data?.data, query, servers.data?.data, t, vault.data?.data]);

  useEffect(() => {
    if (!open) return;
    setQuery('');
    setIndex(0);
    const id = window.setTimeout(() => inputRef.current?.focus(), 0);
    return () => window.clearTimeout(id);
  }, [open]);

  useEffect(() => {
    setIndex(0);
  }, [query]);

  if (!open) return null;
  const active = items[Math.min(index, Math.max(items.length - 1, 0))];

  const go = (item: PaletteItem | undefined, copyOnly: boolean) => {
    if (!item) return;
    if (copyOnly && item.cmd && !item.cmd.startsWith('#')) {
      void navigator.clipboard.writeText(item.cmd).then(
        () => toast.success(copy.copied),
        () => toast.error(copy.copyFailed),
      );
      return;
    }
    if (item.href) navigate(item.href);
    onClose();
  };

  return (
    <div className="np-scrim" role="presentation" onMouseDown={onClose}>
      <div
        className="np-palette"
        role="dialog"
        aria-label={copy.palette}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="pal-in">
          <span aria-hidden="true">$ npc</span>
          <input
            ref={inputRef}
            value={query}
            role="combobox"
            aria-expanded="true"
            aria-controls="np-pal-list"
            placeholder={copy.palettePh}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Escape') {
                event.preventDefault();
                onClose();
              } else if (event.key === 'ArrowDown') {
                event.preventDefault();
                setIndex((value) => Math.min(items.length - 1, value + 1));
              } else if (event.key === 'ArrowUp') {
                event.preventDefault();
                setIndex((value) => Math.max(0, value - 1));
              } else if (event.key === 'Enter') {
                event.preventDefault();
                go(active, event.metaKey || event.ctrlKey);
              }
            }}
          />
        </div>
        <ul id="np-pal-list" role="listbox">
          {items.length === 0 ? (
            <li className="pal-empty">{copy.noMatch}</li>
          ) : (
            items.map((item, itemIndex) => (
              <li
                key={item.id}
                role="option"
                aria-selected={itemIndex === index}
                className={itemIndex === index ? 'pal-item is-active' : 'pal-item'}
                onMouseEnter={() => setIndex(itemIndex)}
                onClick={() => go(item, false)}
              >
                <span className="pal-label">
                  {item.label}
                  {item.hint ? <small>{item.hint}</small> : null}
                </span>
                {item.cmd ? <code>{item.cmd}</code> : null}
              </li>
            ))
          )}
        </ul>
        <p className="pal-foot">
          <span>↑↓</span>
          <span>↵ {copy.open}</span>
          <span>⌘↵ {copy.copyOnly}</span>
          <span>esc {copy.close}</span>
        </p>
      </div>
    </div>
  );
}
